import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import { z } from 'zod'
import { PartnerTier } from './types'
import { AddressState } from '../order'
import { AustralianMobileSchema, DateTimeSchema } from '../common'

extendZodWithOpenApi(z)

export const TenantType = z.enum([
  'LykaStaff',
  'Partner',
  'FulfillmentStaff',
  'D2cCustomer',
  'Guest',
  'TygaCustomer',
  'TygaLead',
  'LykaLead',
  'CustomerCare',
  // Partners
  'Breeder',
  'Vet',
  'Influencer',
  'OtherPartnerBusiness',
  'Affiliate',
  'Research',
  'Rescue',
  'BehaviouristTrainer',
])
export type TenantType = z.infer<typeof TenantType>

export const PartnerTenantType = TenantType.extract([
  'Breeder',
  'Vet',
  'Influencer',
  'OtherPartnerBusiness',
  'Affiliate',
  'Research',
  'Rescue',
  'BehaviouristTrainer',
])
export type PartnerTenantType = z.infer<typeof PartnerTenantType>

export const HowDidYouHearAboutUs = z.object({
  id: z.string().uuid(),
  primaryReason: z.string().min(1),
  secondaryReason: z.string().min(1).optional(),
  otherReason: z.string().min(1).optional(),
}).strict()
export type HowDidYouHearAboutUs = z.infer<typeof HowDidYouHearAboutUs>

export const ExperimentSlug = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
export type ExperimentSlug = z.infer<typeof ExperimentSlug>

/** A person's group in one experiment. Write-once: a slug, once set, never changes. */
export const ExperimentAssignment = z.object({
  variant: z.string().min(1),
  exposedAt: z.string().datetime(),
  /** Set when QA forced the variant outside production; excluded from results. */
  forced: z.boolean().optional(),
}).strict()
export type ExperimentAssignment = z.infer<typeof ExperimentAssignment>

/** Shape of the `users.metadata` JSONB column in the auth service. */
export const UserMetadata = z.object({
  oktaUserId: z.string().min(1).optional(),
  howDidYouHearAboutUs: HowDidYouHearAboutUs.optional(),
  experiments: z.record(ExperimentSlug, ExperimentAssignment).optional(),
}).strict()
export type UserMetadata = z.infer<typeof UserMetadata>

/**
 * `metadata` accepted by the generic user update and upsert paths. Excludes
 * `experiments`, which is only written through the experiment assignment endpoint.
 */
export const UserMetadataUpdate = UserMetadata.omit({ experiments: true })
export type UserMetadataUpdate = z.infer<typeof UserMetadataUpdate>

export const RecordExperimentAssignmentParams = z.object({
  userId: z.string().uuid(),
  slug: ExperimentSlug,
})
export type RecordExperimentAssignmentParams = z.infer<typeof RecordExperimentAssignmentParams>

export const RecordExperimentAssignmentResponse = z.object({
  result: z.object({
    /** False when the user already had an assignment for the slug; `assignment` is then the stored one. */
    created: z.boolean(),
    assignment: ExperimentAssignment,
  }),
})
export type RecordExperimentAssignmentResponse = z.infer<typeof RecordExperimentAssignmentResponse>

/**
 * `metadata` accepted by the migration endpoints. Persisted to
 * `user_id_mapping.legacy_user_id`, not to `users.metadata`.
 */
export const MigrationUserMetadata = z.object({
  legacyUserId: z.number().int().positive(),
}).strict()
export type MigrationUserMetadata = z.infer<typeof MigrationUserMetadata>

export const GetUsersResponse = z.object({
  result: z.array(
    z.object({
      cognito_user_id: z.string().uuid(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.string(),
      phone_number: z.string(),
      role_name: z.string(),
      tenant_type: TenantType,
      metadata: UserMetadata.optional(),
    }),
  ),
})
export type GetUsersResponse = z.infer<typeof GetUsersResponse>

export const BusinessProfile = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  businessName: z.string().nullable(),
  abn: z.string().regex(/^\d{11}$/).nullable(),
  tier: z.nativeEnum(PartnerTier).nullable(),
  discountId: z.string().uuid(),
  hubspotCompanyId: z.string().nullable(),
  deactivatedAt: DateTimeSchema.nullable(),
  deactivatedBy: z.string().uuid().nullable(),
  createdBy: z.string().uuid().nullable(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  deletedAt: DateTimeSchema.nullable(),
})
export type BusinessProfile = z.infer<typeof BusinessProfile>

export const CreateBusinessProfile = BusinessProfile.omit({
  id: true,
  createdBy: true,
  deactivatedAt: true,
  deactivatedBy: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export type CreateBusinessProfile = z.infer<typeof CreateBusinessProfile>

export const UpdateBusinessProfile = BusinessProfile.pick({
  businessName: true,
  abn: true,
  tier: true,
  discountId: true,
  hubspotCompanyId: true,
}).partial()
export type UpdateBusinessProfile = z.infer<typeof UpdateBusinessProfile>

export const GenericBusinessProfileParam = z.object({
  userId: z.string().uuid(),
})
export type GenericBusinessProfileParam = z.infer<typeof GenericBusinessProfileParam>

export const FilterPartnerQuery = z.object({
  tenantType: z.union([PartnerTenantType, PartnerTenantType.array()])
    .optional()
    .transform(value => (value === undefined ? PartnerTenantType.options : [value].flat())),
  offset: z.number({ coerce: true }).int().gte(0).default(0),
  limit: z.number({ coerce: true }).int().gte(1).lte(100).default(25),
})
export type FilterPartnerQuery = z.infer<typeof FilterPartnerQuery>

export const PartnerResponse = z.object({
  cognitoUserId: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string().nullable(),
  tenantType: PartnerTenantType,
  isActivated: z.boolean(),
  createdAt: DateTimeSchema,
  businessProfile: BusinessProfile.pick({
    id: true,
    businessName: true,
    abn: true,
    tier: true,
    discountId: true,
    hubspotCompanyId: true,
  }).nullable(),
})
export type PartnerResponse = z.infer<typeof PartnerResponse>

export const FilterPartnersResponse = z.object({
  meta: z.object({
    offset: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
  }),
  result: PartnerResponse.array(),
})
export type FilterPartnersResponse = z.infer<typeof FilterPartnersResponse>

export const GetPartnerParams = z.object({
  userId: z.string().uuid(),
})
export type GetPartnerParams = z.infer<typeof GetPartnerParams>

export const D2cSearchTenantType = TenantType.extract([
  'D2cCustomer',
  'TygaCustomer',
  'Affiliate',
  'BehaviouristTrainer',
  'Breeder',
  'Influencer',
  'OtherPartnerBusiness',
  'Rescue',
  'Research',
  'Vet',
])
export type D2cSearchTenantType = z.infer<typeof D2cSearchTenantType>

export const D2cCustomerAddressResponse = z.object({
  addressId: z.string().uuid(),
  isDefault: z.boolean(),
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  suburb: z.string(),
  state: z.string(),
  city: z.string().nullish(),
  postcode: z.string(),
  countryCode: z.string(),
  deliveryInstructions: z.string().nullish(),
  companyName: z.string().nullish(),
})
export type D2cCustomerAddressResponse = z.infer<typeof D2cCustomerAddressResponse>

export const D2cCustomerSearchResult = z.object({
  cognitoUserId: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string().nullish(),
  isActivated: z.boolean(),
  tenantType: D2cSearchTenantType,
  createdAt: DateTimeSchema,
  signupCoupon: z.record(z.string(), z.unknown()).nullish(),
  defaultAddress: D2cCustomerAddressResponse.optional(),
})
export type D2cCustomerSearchResult = z.infer<typeof D2cCustomerSearchResult>

export const SearchD2cCustomersResponse = z.object({
  meta: z.object({
    offset: z.number().int(),
    limit: z.number().int(),
  }),
  result: D2cCustomerSearchResult.array(),
})
export type SearchD2cCustomersResponse = z.infer<typeof SearchD2cCustomersResponse>

export const CreatePartnerAddress = z.object({
  companyName: z.string().min(1).optional(),
  address1: z.string().trim().min(1, 'Street address is required'),
  address2: z.string().trim().optional(),
  suburb: z.string().trim().min(1, 'Suburb is required'),
  state: z.nativeEnum(AddressState),
  countryCode: z.string().default('AU'),
  postcode: z.string().regex(/^\d{4}$/).trim(),
  deliveryInstructions: z.string().min(1).optional(),
})
export type CreatePartnerAddress = z.infer<typeof CreatePartnerAddress>

export const CreatePartnerRequest = z.object({
  tenantType: PartnerTenantType.refine((val) => PartnerTenantType.safeParse(val).success, {
    message: "Please select a partner type.",
  }),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phoneNumber: AustralianMobileSchema.optional(),
  businessName: z.string().min(1).optional(),
  abn: z.string().regex(/^\d{11}$/).optional(),
  hubspotCompanyId: z.string()
    .min(1)
    .max(20)
    .regex(/^\d+$/, { message: "HubSpot Company ID must contain only digits" })
    .optional(),
  address: CreatePartnerAddress,
  discountId: z.string().uuid("A discount must be selected"),
})
export type CreatePartnerRequest = z.infer<typeof CreatePartnerRequest>

export const UpdatePartnerRequest = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phoneNumber: AustralianMobileSchema.optional(),
})
export type UpdatePartnerRequest = z.infer<typeof UpdatePartnerRequest>

export const CreatePartnerResponse = z.object({
  result: z.object({
    userId: z.string().uuid(),
    businessProfile: BusinessProfile,
  }),
})
export type CreatePartnerResponse = z.infer<typeof CreatePartnerResponse>

export const RollbackUserRequest = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
})
export type RollbackUserRequest = z.infer<typeof RollbackUserRequest>

export const RollbackCognitoResult = z.enum(['not-found', 'deleted'])
export type RollbackCognitoResult = z.infer<typeof RollbackCognitoResult>

export const RollbackUserResponse = z.object({
  success: z.boolean(),
  cognitoResult: RollbackCognitoResult,
})
export type RollbackUserResponse = z.infer<typeof RollbackUserResponse>
